/**
 * Event Countdown Component
 * Fetches upcoming event from REST API and displays live countdown
 */

class EventCountdown {
    constructor(element) {
        this.element = element;
        this.restUrl = element.dataset.restUrl;
        this.countdownInterval = null;
        this.eventData = null;

        this.init();
    }

    init() {
        this.fetchEvent();
    }

    fetchEvent() {
        fetch(this.restUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                // Check if we have event data
                if (data.success && data.event) {
                    this.eventData = data.event;
                    this.displayEvent(data);

                    // Start countdown if not today
                    if (!data.countdown.is_today) {
                        this.startCountdown();
                    }
                } else {
                    // No events found - expected during off-season
                    console.log('Event countdown: No upcoming events found (normal during off-season)');
                    this.displayNoEvent();
                }
            })
            .catch(error => {
                // Only actual errors (network failures, server errors)
                console.log('Event countdown: Could not fetch event data -', error.message);
                this.displayNoEvent();
            });
    }

    displayEvent(data) {
        const event = data.event;
        const countdown = data.countdown;
        const labels = data.labels || {};

        let html = '<p class="event-countdown__text">';
        html += (labels.nextEvent || 'Next upcoming event:') + ' ';
        html += '<br />' +
            '<strong>' + this.escapeHtml(event.title) + '</strong>';

        if (event.site_name && event.site_name !== event.title) {
            html += ' ' + (labels.at || 'at') + ' ' + this.escapeHtml(event.site_name);
        }

        html += '</p>';

        if (countdown.is_today) {
            html += '<div class="event-countdown__today">';
            html += '<p class="event-countdown__today-message">' + countdown.message + '</p>';
            html += '</div>';
        } else {
            html += '<div class="event-countdown__counter" data-timestamp="' + event.timestamp + '">';
            html += this.createCountdownItem('days', countdown.days, countdown.labels);
            html += this.createCountdownItem('hours', countdown.hours, countdown.labels);
            html += this.createCountdownItem('minutes', countdown.minutes, countdown.labels);
            html += this.createCountdownItem('seconds', countdown.seconds, countdown.labels);
            html += '</div>';
        }

        const contentEl = this.element.querySelector('.event-countdown__content');
        if (contentEl) {
            contentEl.innerHTML = html;
        }
    }

    createCountdownItem(unit, value, labels) {
        const label = labels && labels[unit] ? labels[unit] : unit;
        const formattedValue = this.formatWithLeadingZero(value);
        return `<div class="event-countdown__item">
			<span class="event-countdown__value" data-unit="${unit}">${formattedValue}</span>
			<span class="event-countdown__label">${label}</span>
		</div>`;
    }

    displayNoEvent() {
        const contentEl = this.element.querySelector('.event-countdown__content');
        if (contentEl) {
            contentEl.innerHTML = '';
        }
    }

    startCountdown() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }

        this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
    }

    updateCountdown() {
        const counterEl = this.element.querySelector('.event-countdown__counter');
        if (!counterEl || !this.eventData) return;

        const eventTimestamp = parseInt(counterEl.dataset.timestamp, 10) * 1000;
        const now = Date.now();
        const diff = eventTimestamp - now;

        if (diff <= 0) {
            // Event has started, refresh data
            clearInterval(this.countdownInterval);
            this.fetchEvent();
            return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        const daysEl = counterEl.querySelector('[data-unit="days"]');
        const hoursEl = counterEl.querySelector('[data-unit="hours"]');
        const minutesEl = counterEl.querySelector('[data-unit="minutes"]');
        const secondsEl = counterEl.querySelector('[data-unit="seconds"]');

        if (daysEl) daysEl.textContent = this.formatWithLeadingZero(days);
        if (hoursEl) hoursEl.textContent = this.formatWithLeadingZero(hours);
        if (minutesEl) minutesEl.textContent = this.formatWithLeadingZero(minutes);
        if (secondsEl) secondsEl.textContent = this.formatWithLeadingZero(seconds);
    }

    formatWithLeadingZero(num) {
        return num < 10 ? '0' + num : num.toString();
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

// Initialize all event countdown instances
document.addEventListener('DOMContentLoaded', () => {
    const countdownElements = document.querySelectorAll('.event-countdown');
    countdownElements.forEach(element => {
        new EventCountdown(element);
    });
});

